import { useCallback, useState, type FormEvent } from 'react';
import { api, ApiError } from '../api/client';
import type { Account, AiStatus, Category, Device, PaymentMethod } from '../api/types';
import { Modal } from '../components/Modal';
import { formatDateTime, krw } from '../lib/format';
import { useAsync } from '../lib/useAsync';
import { useApp } from '../state';

type Message = { text: string; restore?: { path: string; id: string } } | null;
const keywordList = (value: string) => [...new Set(value.split(/[,，\n]/).map((s) => s.trim()).filter(Boolean))].slice(0, 30);

/**
 * 분류·수단·계좌 관리. 삭제는 보관(archived)이며 기존 거래·통계는 그대로다. 같은 이름의 삭제 항목은 새로 만들지 않고 복원한다.
 */
export function SettingsPage() {
  const { catalog, reloadCatalog, changed, toast } = useApp();
  const [message, setMessage] = useState<Message>(null);
  const [editing, setEditing] = useState<{ type: 'category'; item: Category } | { type: 'payment'; item: PaymentMethod } | null>(null);

  const after = async (text: string) => {
    await reloadCatalog();
    changed();
    setMessage({ text });
    toast(text);
  };

  async function run(action: () => Promise<unknown>, success: string, restorePath?: string) {
    try {
      await action();
      await after(success);
      return true;
    } catch (e) {
      if (e instanceof ApiError) {
        const details = e.details as { existingId?: string; archived?: boolean } | undefined;
        setMessage({ text: e.message, restore: details?.archived && details.existingId && restorePath ? { path: restorePath, id: details.existingId } : undefined });
        if (e.status === 409) await reloadCatalog();
      } else setMessage({ text: '처리하지 못했습니다.' });
      return false;
    }
  }

  async function restore(path: string, id: string) {
    const item = path === 'categories' ? catalog.categories.find((c) => c.id === id) : path === 'payment-methods' ? catalog.paymentMethods.find((p) => p.id === id) : catalog.accounts.find((a) => a.id === id);
    if (!item) return;
    await run(() => api.post(`/${path}/${id}/restore`, {}, { ifMatch: item.revision }), '복원했습니다. 새 기록에서 다시 선택할 수 있습니다.');
  }
  const archive = (path: string, item: { id: string; revision: number }) =>
    run(() => api.delete(`/${path}/${item.id}`, { ifMatch: item.revision }), '삭제했습니다. 과거 거래·통계는 유지되며 새 입력에서는 제외됩니다.');

  const tops = catalog.categories.filter((c) => c.parentId === null);
  const activeTops = tops.filter((c) => c.active);

  return (
    <section>
      <article className="card">
        <h2>내게 맞는 항목 만들기</h2>
        <p className="muted">추가한 항목은 입력창·내역 필터에 바로 반영되고, 모든 기기에 동기화됩니다.</p>
      </article>
      <div className="settings-grid">
        <CategoryAddForm onSubmit={(body) => run(() => api.post('/categories', body), '대분류를 추가했습니다.', 'categories')} />
        <SubcategoryAddForm parents={activeTops} onSubmit={(body) => run(() => api.post('/categories', body), '소분류를 추가했습니다.', 'categories')} />
        <PaymentAddForm onSubmit={(body) => run(() => api.post('/payment-methods', body), '수단을 추가했습니다.', 'payment-methods')} />
      </div>
      {message && (
        <p className="notice" role="status">
          {message.text}
          {message.restore && (
            <button className="text restore" onClick={() => void restore(message.restore!.path, message.restore!.id)}>
              삭제된 항목 복원하기
            </button>
          )}
        </p>
      )}

      <article className="card">
        <h2>등록된 분류</h2>
        <p className="hint">분류를 펼치면 수정·삭제할 수 있습니다. 대분류를 삭제하면 소분류도 새 입력에서 제외되고, 복원해도 따로 삭제한 소분류는 복원되지 않습니다.</p>
        {tops.map((c) => (
          <details className={`catalog-item ${c.archivedAt ? 'catalog-archived' : ''}`} key={c.id}>
            <summary>
              <b>
                {c.name}
                {c.archivedAt ? ' · 삭제됨' : ''}
              </b>
              <span>
                {c.kind === 'income' ? (c.incomeGroup === 'fixed' ? '고정수입' : '부가수입') : '지출'} · 소분류 {catalog.categories.filter((s) => s.parentId === c.id && !s.archivedAt).length}개
              </span>
            </summary>
            <div className="catalog-controls">
              <p className="hint">키워드: {c.keywords.join(', ') || '없음'}</p>
              <span className="row-actions">
                <button className="catalog-action" onClick={() => setEditing({ type: 'category', item: c })}>
                  수정
                </button>
                {c.archivedAt ? (
                  <button className="catalog-action" onClick={() => void restore('categories', c.id)}>
                    복원
                  </button>
                ) : (
                  <button className="catalog-action danger" onClick={() => void archive('categories', c)}>
                    삭제
                  </button>
                )}
              </span>
            </div>
            <div className="catalog-tags">
              {catalog.categories
                .filter((s) => s.parentId === c.id)
                .map((s) => (
                  <span key={s.id} className={s.archivedAt ? 'catalog-archived' : ''}>
                    {s.name}
                    {s.archivedAt ? ' · 삭제됨' : ''}
                    {s.keywords.length > 0 && <small>{s.keywords.join(', ')}</small>}
                    <button className="catalog-action" onClick={() => setEditing({ type: 'category', item: s })}>
                      수정
                    </button>
                    {!c.archivedAt &&
                      (s.archivedAt ? (
                        <button className="catalog-action" onClick={() => void restore('categories', s.id)}>
                          복원
                        </button>
                      ) : (
                        <button className="catalog-action danger" onClick={() => void archive('categories', s)}>
                          삭제
                        </button>
                      ))}
                  </span>
                ))}
            </div>
          </details>
        ))}

        <h2 className="catalog-payment-heading">등록된 결제·입금수단</h2>
        {catalog.paymentMethods.map((p) => (
          <div className={`catalog-line ${p.archivedAt ? 'catalog-archived' : ''}`} key={p.id}>
            <span>
              {p.kind === 'income' ? '입금' : '결제'} · {p.name}
              {p.archivedAt ? ' · 삭제됨' : ''}
              {p.keywords.length > 0 && <small> {p.keywords.join(', ')}</small>}
            </span>
            <span className="row-actions">
              <button className="catalog-action" onClick={() => setEditing({ type: 'payment', item: p })}>
                수정
              </button>
              {p.archivedAt ? (
                <button className="catalog-action" onClick={() => void restore('payment-methods', p.id)}>
                  복원
                </button>
              ) : (
                <button className="catalog-action danger" onClick={() => void archive('payment-methods', p)}>
                  삭제
                </button>
              )}
            </span>
          </div>
        ))}
      </article>

      <Accounts accounts={catalog.accounts} run={run} restore={(id) => void restore('accounts', id)} archive={(a) => void archive('accounts', a)} />
      <AiSettings />
      <Devices />

      <Modal open={editing !== null} onClose={useCallback(() => setEditing(null), [])} title="항목 수정">
        {editing && (
          <EditForm
            editing={editing}
            onSubmit={async (body) => {
              const path = editing.type === 'category' ? 'categories' : 'payment-methods';
              const ok = await run(() => api.patch(`/${path}/${editing.item.id}`, body, { ifMatch: editing.item.revision }), '수정했습니다. 과거 기록은 당시 이름으로 남습니다.', path);
              if (ok) setEditing(null);
            }}
          />
        )}
      </Modal>
    </section>
  );
}

function CategoryAddForm({ onSubmit }: { onSubmit: (body: unknown) => Promise<boolean> }) {
  const [kind, setKind] = useState<'expense' | 'income'>('expense');
  const [name, setName] = useState('');
  const [incomeGroup, setIncomeGroup] = useState<'additional' | 'fixed'>('additional');
  const [keywords, setKeywords] = useState('');
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (await onSubmit({ kind, name, keywords: keywordList(keywords), ...(kind === 'income' && { incomeGroup }) })) {
      setName('');
      setKeywords('');
    }
  }
  return (
    <form className="card" onSubmit={(e) => void submit(e)}>
      <h2>대분류 추가</h2>
      <label>
        거래 종류
        <select value={kind} onChange={(e) => setKind(e.target.value as 'expense' | 'income')}>
          <option value="expense">지출</option>
          <option value="income">수입</option>
        </select>
      </label>
      <label>
        대분류 이름
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} required placeholder="예: 반려동물" />
      </label>
      {kind === 'income' && (
        <label>
          수입 집계 구분
          <select value={incomeGroup} onChange={(e) => setIncomeGroup(e.target.value as 'additional' | 'fixed')}>
            <option value="additional">부가수입</option>
            <option value="fixed">고정수입</option>
          </select>
        </label>
      )}
      <label>
        추천 키워드 (선택)
        <input value={keywords} onChange={(e) => setKeywords(e.target.value)} maxLength={300} placeholder="예: 강아지, 고양이" />
      </label>
      <button className="primary">대분류 추가</button>
      <p className="hint">새 대분류에는 소분류를 하나 이상 추가해야 기록에 쓸 수 있습니다.</p>
    </form>
  );
}

function SubcategoryAddForm({ parents, onSubmit }: { parents: Category[]; onSubmit: (body: unknown) => Promise<boolean> }) {
  const [parentId, setParentId] = useState('');
  const [name, setName] = useState('');
  const [keywords, setKeywords] = useState('');
  const parent = parents.find((p) => p.id === parentId);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!parent) return;
    if (await onSubmit({ kind: parent.kind, parentId, name, keywords: keywordList(keywords) })) {
      setName('');
      setKeywords('');
    }
  }
  return (
    <form className="card" onSubmit={(e) => void submit(e)}>
      <h2>소분류 추가</h2>
      <label>
        상위 대분류
        <select value={parentId} onChange={(e) => setParentId(e.target.value)} required>
          <option value="">{parents.length ? '선택 필요' : '대분류를 추가하거나 복원해주세요'}</option>
          {parents.map((p) => (
            <option key={p.id} value={p.id}>
              {p.kind === 'income' ? '수입' : '지출'} · {p.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        소분류 이름
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} required placeholder="예: 사료" />
      </label>
      <label>
        추천 키워드 (선택)
        <input value={keywords} onChange={(e) => setKeywords(e.target.value)} maxLength={300} />
      </label>
      <button className="primary">소분류 추가</button>
    </form>
  );
}

function PaymentAddForm({ onSubmit }: { onSubmit: (body: unknown) => Promise<boolean> }) {
  const [kind, setKind] = useState<'expense' | 'income'>('expense');
  const [name, setName] = useState('');
  const [keywords, setKeywords] = useState('');
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (await onSubmit({ kind, name, keywords: keywordList(keywords) })) {
      setName('');
      setKeywords('');
    }
  }
  return (
    <form className="card" onSubmit={(e) => void submit(e)}>
      <h2>결제·입금수단 추가</h2>
      <label>
        사용 구분
        <select value={kind} onChange={(e) => setKind(e.target.value as 'expense' | 'income')}>
          <option value="expense">지출 결제수단</option>
          <option value="income">수입 입금수단</option>
        </select>
      </label>
      <label>
        이름
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} required placeholder="예: 현대카드" />
      </label>
      <label>
        추천 키워드 (선택)
        <input value={keywords} onChange={(e) => setKeywords(e.target.value)} maxLength={300} placeholder="예: 현대, 현카" />
      </label>
      <button className="primary">수단 추가</button>
    </form>
  );
}

function EditForm({ editing, onSubmit }: { editing: { type: 'category'; item: Category } | { type: 'payment'; item: PaymentMethod }; onSubmit: (body: unknown) => Promise<void> }) {
  const item = editing.item;
  const [name, setName] = useState(item.name);
  const [keywords, setKeywords] = useState(item.keywords.join(', '));
  const isIncomeTop = editing.type === 'category' && editing.item.kind === 'income' && editing.item.parentId === null;
  const [incomeGroup, setIncomeGroup] = useState(editing.type === 'category' ? (editing.item.incomeGroup ?? 'additional') : 'additional');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit({ name, keywords: keywordList(keywords), ...(isIncomeTop && { incomeGroup }) });
      }}
    >
      <label>
        이름
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} required />
      </label>
      <label>
        추천 키워드
        <input value={keywords} onChange={(e) => setKeywords(e.target.value)} maxLength={300} />
      </label>
      {isIncomeTop && (
        <label>
          수입 집계 구분
          <select value={incomeGroup} onChange={(e) => setIncomeGroup(e.target.value as 'fixed' | 'additional')}>
            <option value="additional">부가수입</option>
            <option value="fixed">고정수입</option>
          </select>
        </label>
      )}
      <p className="hint">이름을 바꿔도 과거 기록은 기록 당시 이름으로 보입니다. 고정/부가 구분 변경은 이후 기록부터 적용됩니다.</p>
      <button className="primary full save">저장</button>
    </form>
  );
}

function Accounts({ accounts, run, restore, archive }: { accounts: Account[]; run: (a: () => Promise<unknown>, s: string, r?: string) => Promise<boolean>; restore: (id: string) => void; archive: (a: Account) => void }) {
  const [name, setName] = useState('');
  const [accountType, setAccountType] = useState<Account['accountType']>('bank');
  const TYPE = { bank: '은행 계좌', cash: '현금', card: '카드' } as const;
  return (
    <article className="card spaced">
      <h2>계좌 (이체용)</h2>
      <p className="hint">내 계좌 사이 이동·적금 납입·카드 대금 결제는 ‘이체’로 기록하며 수입/지출 합계에서 제외됩니다. 잔액은 관리하지 않습니다.</p>
      <form
        className="inline-form"
        onSubmit={async (e) => {
          e.preventDefault();
          if (await run(() => api.post('/accounts', { name, accountType }), '계좌를 추가했습니다.', 'accounts')) setName('');
        }}
      >
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} required placeholder="예: 급여통장" aria-label="계좌 이름" />
        <select value={accountType} onChange={(e) => setAccountType(e.target.value as Account['accountType'])} aria-label="계좌 종류">
          <option value="bank">은행 계좌</option>
          <option value="card">카드</option>
          <option value="cash">현금</option>
        </select>
        <button className="primary">추가</button>
      </form>
      {accounts.map((a) => (
        <div className={`catalog-line ${a.archivedAt ? 'catalog-archived' : ''}`} key={a.id}>
          <span>
            {TYPE[a.accountType]} · {a.name}
            {a.archivedAt ? ' · 삭제됨' : ''}
          </span>
          {a.archivedAt ? (
            <button className="catalog-action" onClick={() => restore(a.id)}>
              복원
            </button>
          ) : (
            <button className="catalog-action danger" onClick={() => archive(a)}>
              삭제
            </button>
          )}
        </div>
      ))}
    </article>
  );
}

function Devices() {
  const { toast } = useApp();
  const devices = useAsync(() => api.get<{ data: Device[] }>('/devices').then((r) => r.data), []);
  const [error, setError] = useState<string | null>(null);
  async function revoke(device: Device) {
    setError(null);
    try {
      if (device.current) {
        await api.post('/session/logout');
        location.reload();
        return;
      }
      await api.delete(`/devices/${device.id}`);
      toast('기기 연결을 해제했습니다. 해당 기기는 즉시 차단됩니다.');
      devices.reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '처리하지 못했습니다.');
    }
  }
  return (
    <article className="card spaced">
      <h2>연결된 기기</h2>
      <p className="hint">분실하거나 더 이상 쓰지 않는 기기는 여기서 바로 해제하세요. 새 기기는 서버 CLI에서 페어링 코드를 발급해 연결합니다.</p>
      {error && <p className="error">{error}</p>}
      {devices.data?.map((d) => (
        <div className={`catalog-line ${d.revokedAt || Date.parse(d.expiresAt) < Date.now() ? 'catalog-archived' : ''}`} key={d.id}>
          <span>
            {d.platform === 'ios' ? 'iPhone' : '웹'} · {d.name}
            {d.current ? ' · 지금 이 브라우저' : ''}
            <small>
              {' '}
              마지막 사용 {formatDateTime(d.lastSeenAt)} · 만료 {formatDateTime(d.expiresAt)}
              {d.revokedAt ? ` · 해제됨 ${formatDateTime(d.revokedAt)}` : ''}
            </small>
          </span>
          {!d.revokedAt && (
            <button className="catalog-action danger" onClick={() => void revoke(d)}>
              {d.current ? '로그아웃' : '연결 해제'}
            </button>
          )}
        </div>
      ))}
    </article>
  );
}

/** AI 사용 현황과 외부 AI로 보내는 항목 안내(설계서 8장). 한도·키는 서버 설정에서 바꾼다. */
function AiSettings() {
  const status = useAsync(() => api.get<{ data: AiStatus }>('/ai/status').then((r) => r.data), []);
  const s = status.data;
  return (
    <article className="card spaced">
      <h2>AI 입력·분석</h2>
      {!s ? (
        <p className="muted">{status.error ? status.error.message : '불러오는 중…'}</p>
      ) : !s.enabled ? (
        <p className="muted">꺼져 있습니다. 서버에 Gemini API 키(GEMINI_API_KEY_FILE)를 설정하면 쓸 수 있어요. 수동 입력과 통계는 그대로 동작합니다.</p>
      ) : (
        s.usage && (
          <>
            <p className="muted">
              {s.provider} · {s.model}
            </p>
            <p>
              이번 달({s.usage.month}) 추정 사용액 <b>{krw(s.usage.estimatedUsedKrw)}</b> / 한도 {krw(s.usage.monthlyBudgetKrw)} · 오늘 한 줄 입력 {s.usage.parseToday}/{s.usage.parseDailyLimit}회 · 분석{' '}
              {s.usage.analysisToday}/{s.usage.analysisDailyLimit}회
            </p>
            <p className="hint">추정액은 넉넉한 단가로 계산한 값이며 한도에 닿으면 AI만 멈춥니다. 실제 청구액은 Google AI Studio 결제 화면에서 확인하세요.</p>
          </>
        )
      )}
      <p className="hint">
        외부 AI(Google Gemini)로 보내는 항목: 한 줄 입력 원문, 기준 날짜, 현재 활성 분류·수단의 이름과 키워드. 분석은 기간 합계·분류별 금액·이전 기간 비교, 큰 지출과 중복 후보 거래의 날짜·금액·사용처·분류,
        반복 항목 이름. 메모·계좌 정보·기기 정보는 보내지 않습니다. AI는 기록을 저장·삭제하지 않고, 결과는 확인한 뒤에만 저장됩니다.
      </p>
    </article>
  );
}
